import type { Editor } from "@tiptap/core";

export interface DocumentTextEdit {
  find: string;
  replace: string;
}

/** Build one transaction before dispatch: any ambiguous/stale edit leaves the editor intact. */
export function applyDocumentTextEdits(
  editor: Editor,
  edits: DocumentTextEdit[],
  rich: {
    blocks?: Record<string, unknown>[];
    placement?: "append" | "prepend" | "replace";
    formatting?: {
      find: string;
      marks: { type: string; attrs?: Record<string, unknown> }[];
    }[];
  } = {},
) {
  if (
    (!edits.length && !rich.blocks?.length && !rich.formatting?.length) ||
    edits.length > 30
  )
    throw new Error("Invalid document edit");
  const transaction = editor.state.tr;
  for (const edit of edits) {
    const matches: { from: number; to: number }[] = [];
    transaction.doc.descendants((node, pos) => {
      if (!node.isTextblock) return true;
      const text = node.textBetween(0, node.content.size, "", "\uFFFC");
      for (
        let index = text.indexOf(edit.find);
        index >= 0;
        index = text.indexOf(edit.find, index + 1)
      ) {
        matches.push({
          from: pos + 1 + index,
          to: pos + 1 + index + edit.find.length,
        });
      }
      return false;
    });
    if (!edit.find || matches.length !== 1)
      throw new Error(
        "The passage changed or appears several times. Prepare a new proposal with one exact text passage per edit.",
      );
    const match = matches[0]!;
    // Leave common text (and its existing marks) untouched.
    let prefix = 0;
    let suffix = 0;
    while (
      prefix < edit.find.length &&
      prefix < edit.replace.length &&
      edit.find[prefix] === edit.replace[prefix]
    )
      prefix++;
    while (
      suffix < edit.find.length - prefix &&
      suffix < edit.replace.length - prefix &&
      edit.find[edit.find.length - 1 - suffix] ===
        edit.replace[edit.replace.length - 1 - suffix]
    )
      suffix++;
    if (prefix !== edit.find.length || prefix !== edit.replace.length)
      transaction.insertText(
        edit.replace.slice(prefix, edit.replace.length - suffix),
        match.from + prefix,
        match.to - suffix,
      );
  }
  const blocks = (rich.blocks ?? []).map((json) => {
    const node = editor.state.schema.nodeFromJSON(json);
    node.check();
    return node;
  });
  if (blocks.length) {
    if (rich.placement === "replace")
      transaction.replaceWith(0, transaction.doc.content.size, blocks);
    else
      transaction.insert(
        rich.placement === "prepend" ? 0 : transaction.doc.content.size,
        blocks,
      );
  }
  for (const format of rich.formatting ?? []) {
    const matches: { from: number; to: number }[] = [];
    transaction.doc.descendants((node, pos) => {
      if (!node.isTextblock) return true;
      const text = node.textBetween(0, node.content.size, "", "\uFFFC");
      for (
        let i = text.indexOf(format.find);
        i >= 0;
        i = text.indexOf(format.find, i + 1)
      )
        matches.push({
          from: pos + 1 + i,
          to: pos + 1 + i + format.find.length,
        });
      return false;
    });
    if (!format.find || matches.length !== 1)
      throw new Error("The formatting passage must match exactly once.");
    for (const mark of format.marks) {
      const type = editor.state.schema.marks[mark.type];
      if (!type) throw new Error("Unsupported document formatting");
      const match = matches[0]!;
      if (mark.type === "textStyle") {
        // A color change must retain an existing font/size on each text span.
        const spans: {
          from: number;
          to: number;
          attrs: Record<string, unknown>;
        }[] = [];
        transaction.doc.nodesBetween(match.from, match.to, (node, position) => {
          if (node.isText)
            spans.push({
              from: Math.max(position, match.from),
              to: Math.min(position + node.nodeSize, match.to),
              attrs: {
                ...node.marks.find((existing) => existing.type === type)?.attrs,
                ...mark.attrs,
              },
            });
        });
        for (const span of spans)
          transaction.addMark(span.from, span.to, type.create(span.attrs));
      } else transaction.addMark(match.from, match.to, type.create(mark.attrs));
    }
  }
  transaction.doc.check();
  editor.view.dispatch(transaction);
}
